import { useMemo } from 'react';

import { useOperations } from '@frontend/hooks';

// Interim: the dedicated `manage_private_agents` user attribute does not exist in
// Turnstile yet, so we gate on `view_agents` (an Opal user-level attribute held by
// essentially every user) behind this single constant — matching the intent that
// all users get private-agent capability by default. When `manage_private_agents`
// is provisioned in Turnstile and added to UserOperations + OPERATIONS_NAMES,
// change ONLY this constant.
const PRIVATE_AGENTS_USER_OPERATION = 'view_agents' as const;

/**
 * Permissions for the private-agents feature.
 *
 * `canManagePrivateAgents` requires BOTH the Advanced-tier instance capability
 * (`private_agents_enabled`) AND the user op — mirroring the established
 * useToolPermissions / useSpecializedAgentPermissions pattern.
 */
export function usePrivateAgentPermissions() {
  const { canUser, canInstance } = useOperations();

  return useMemo(() => {
    const hasPrivateAgentsCapability = canInstance(['private_agents_enabled']);
    const canManagePrivateAgents =
      hasPrivateAgentsCapability && canUser([PRIVATE_AGENTS_USER_OPERATION]);

    return { hasPrivateAgentsCapability, canManagePrivateAgents };
  }, [canUser, canInstance]);
}
